/**
 * StoreMap — visualização das lojas do tenant num mapa interativo (TH-55102).
 *
 * Soberania (ADR-006): tiles OpenStreetMap via CartoCDN (Positron/Dark
 * Matter), sem Google Maps, sem Mapbox. CartoCDN é apenas CDN — os tiles
 * são derivados de OSM, gratuitos e sem chave de API.
 *
 * Lojas sem latitude/longitude ficam fora do mapa; a página exibe um
 * contador discreto. Bounds calculado a partir de todas as lojas com
 * coords; fallback pra centro do Brasil quando nenhuma tem.
 */

import { useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import L from "leaflet";

import type { Store } from "@/pages/stores/StoreFormDialog";
import { useTheme } from "@/hooks/useTheme";

import "leaflet/dist/leaflet.css";

// Workaround do leaflet com bundlers: os ícones default usam paths
// relativos que quebram no Vite. Apontamos pros assets via CDN do
// próprio leaflet (mesma origem do package, não big-tech US).
const DefaultIcon = L.icon({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

interface MapStore {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  lat: number;
  lng: number;
}

function coordToNumber(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function toMapStore(s: Store): MapStore | null {
  const lat = coordToNumber(s.latitude);
  const lng = coordToNumber(s.longitude);
  if (lat === null || lng === null) return null;
  return {
    id: s.id,
    name: s.name,
    address: s.address,
    city: s.city,
    state: s.state,
    lat,
    lng,
  };
}

/** Ajusta o viewport para enquadrar todas as lojas (com padding). */
function FitBounds({ markers }: { markers: MapStore[] }) {
  const map = useMap();
  useEffect(() => {
    if (markers.length === 0) return;
    if (markers.length === 1) {
      map.setView([markers[0].lat, markers[0].lng], 13);
      return;
    }
    const bounds = L.latLngBounds(
      markers.map((m) => [m.lat, m.lng] as [number, number]),
    );
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [map, markers]);
  return null;
}

export interface StoreMapProps {
  stores: Store[];
}

export function StoreMap({ stores }: StoreMapProps) {
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);

  const markers = stores
    .map(toMapStore)
    .filter((m): m is MapStore => m !== null);

  const missingCount = stores.length - markers.length;

  // Carto Positron (light) e Dark Matter (dark) — OSM-based, sem chave.
  // Atribuição obrigatória conforme OSM ODbL.
  const tileUrl =
    theme === "light"
      ? "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
      : "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";
  const attribution =
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';

  if (markers.length === 0) {
    return (
      <div
        ref={containerRef}
        className="flex h-[480px] w-full flex-col items-center justify-center rounded-md border border-[var(--color-border)] bg-[var(--color-muted)]/30 p-6 text-center"
        data-testid="store-map-empty"
      >
        <p className="text-sm font-medium text-[var(--color-foreground)]">
          Nenhuma loja com coordenadas
        </p>
        <p className="mt-1 max-w-md text-xs text-[var(--color-muted-foreground)]">
          Cadastre latitude e longitude das lojas para visualizá-las no mapa.
          Edite uma loja na aba "Lista" e preencha os campos Latitude / Longitude.
        </p>
      </div>
    );
  }

  // Centro inicial: primeiro marker. FitBounds ajusta depois.
  const initialCenter: [number, number] = [markers[0].lat, markers[0].lng];

  return (
    <div className="flex flex-col gap-2" data-testid="store-map">
      <div
        ref={containerRef}
        className="h-[480px] w-full overflow-hidden rounded-md border border-[var(--color-border)]"
      >
        <MapContainer
          center={initialCenter}
          zoom={6}
          scrollWheelZoom
          style={{ height: "100%", width: "100%" }}
        >
          {/* Key força recriar a layer ao trocar tema (Carto não tem fade in-place). */}
          <TileLayer
            key={theme}
            url={tileUrl}
            attribution={attribution}
            maxZoom={19}
          />
          <FitBounds markers={markers} />
          {markers.map((m) => (
            <Marker key={m.id} position={[m.lat, m.lng]}>
              <Popup>
                <div className="flex flex-col gap-1">
                  <strong>{m.name}</strong>
                  {m.address ? <span>{m.address}</span> : null}
                  {m.city || m.state ? (
                    <span>
                      {[m.city, m.state].filter(Boolean).join(" - ")}
                    </span>
                  ) : null}
                  <Link
                    to="/stores/$storeId"
                    params={{ storeId: m.id }}
                    className="mt-1 text-[var(--color-primary)] underline"
                  >
                    Abrir loja
                  </Link>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {missingCount > 0 ? (
        <p
          className="text-xs text-[var(--color-muted-foreground)]"
          data-testid="store-map-missing"
        >
          {missingCount}{" "}
          {missingCount === 1 ? "loja sem" : "lojas sem"} endereço geocodificado
          (não exibida{missingCount === 1 ? "" : "s"} no mapa).
        </p>
      ) : null}
    </div>
  );
}
